import h from 'hyperscript';
import iconVisibilityOff from '@disy/cadenza-icons/visibility-off.svg';

import { AFTER_LOAD_DATA, BEFORE_LOAD_DATA } from 'ui/data-loading-event-types';
import { createIconButton } from 'ui/button/button';

import { getColumnIndexForBackend } from 'cadenza/ui/base-table/base-table';
import { PIVOT_CHANGE } from 'cadenza/result-table/toolbar/table-settings/event-types';
import { on, unByKey } from 'cadenza/utils/event-util';
import { icon } from 'cadenza/utils/icon/icon';
import { PIVOT_SORT_CHANGE } from 'cadenza/result-table/pivot-table-features/event-types';
import {
  initColumnHeaderContextMenu
} from 'cadenza/ui/base-table/column-header-context-menu/column-header-context-menu';
import { isRowColumn } from 'cadenza/result-table/pivot-table-features/pivot-table-utils';
import { createHorizontalSortIcon } from 'cadenza/workbook/tables/sort-icon/sort-icon';

import './pivot-table-features.css';
import i18n from './messages.properties';

const COMPONENT_NAME = 'd-pivot-table-features';
const HORIZONTAL_SORTING_BUTTONS_HEIGHT = 24; // px

export const VIRTUAL_VALUES_DIMENSION = 'VIRTUAL_VALUES_DIMENSION';
const BUTTON_CONTAINER = 'pivot-sort-button-container';

/**
 * @typedef {function(number): MenuContentElement} PivoteHeaderContextMenuContentProvider
 */

export class PivotTableFeatures extends HTMLElement {

  _buttonsContainer = null;
  _tableResizeObserver = null;
  /** @type {OlapPivotTable} */
  #olapPivotTable;
  /** @type {PivoteHeaderContextMenuContentProvider[]} */
  #pivotHeaderContextMenuContentProviders = [];

  /**
   * @param {OlapPivotTable} olapPivotTable
   */
  constructor (olapPivotTable) {
    super();
    this.classList.add(COMPONENT_NAME);
    this.#olapPivotTable = olapPivotTable;
    this._eventKeys = [
      on(this.#olapPivotTable, BEFORE_LOAD_DATA, () => this._removeButtonsContainer()),
      on(this.#olapPivotTable, AFTER_LOAD_DATA, () => this._renderPivotSortButtons())
    ];

    if (olapPivotTable.showColumnHideButton) {
      olapPivotTable.addHeaderContextMenuContentProvider(this.#createContextMenuContentProvider());
    }
  }

  disconnectedCallback () {
    this._tableResizeObserver?.disconnect(); // Observer is only present when having sort buttons
    unByKey(this._eventKeys);
  }

  /**
   * Adds a menu content provider that extends the column header context menu with an additional menu entry for pivot
   * header buttons.
   * @param {PivoteHeaderContextMenuContentProvider} provider
   */
  addPivotHeaderContextMenuContentProvider (provider) {
    this.#pivotHeaderContextMenuContentProviders.push(provider);
  }

  /**
   * Removes buttons container. By using this function we make sure that it is cleared from the DOM
   * as well.
   */
  _removeButtonsContainer () {
    if (this._buttonsContainer) {
      this._buttonsContainer.remove();
      this._buttonsContainer = null;
    }
  }

  _renderPivotSortButtons () {
    if (!this.#olapPivotTable.tableConfiguration.pivotConfiguration?.pivotTableConfig
      || this.#olapPivotTable.isRenderForPrint()) {
      return;
    }

    this._removeButtonsContainer();
    this._tableResizeObserver?.disconnect();
    this._tableResizeObserver = null;

    const columnsForButtons = this._getColumnsForButtons();
    if (columnsForButtons.length) {
      this._buttonsContainer = h(`.${BUTTON_CONTAINER}`, [
        h(`.${BUTTON_CONTAINER}--space`),
        h(`.${BUTTON_CONTAINER}--buttons`, this._createButtons(columnsForButtons))
      ]);

      this._updateRowTotalColumnWidth();
      const rowTotalColumnHeaderCell = this._getFirstRowTotalColumnHeaderCell();
      if (rowTotalColumnHeaderCell) {
        this._tableResizeObserver = new ResizeObserver(() => this._updateRowTotalColumnWidth());
        this._tableResizeObserver.observe(rowTotalColumnHeaderCell);
      }

      this.#olapPivotTable.getScrollHeadInner().append(this._buttonsContainer);
    }
  }

  _updateRowTotalColumnWidth () {
    // Sort buttons of pivot columns should be positioned on the last pivot column value. To not
    // position them over a possibly shown row total column, we make the row total column width
    // available in CSS. We do this using a named CSS property, so that it is easier to find where
    // this width is determined, compared to a unnamed pixel value.
    if (this._buttonsContainer) {
      this._buttonsContainer.style.setProperty(
        '--dynamic-row-total-column-width',
        `${this._getFirstRowTotalColumnHeaderCell()?.getBoundingClientRect().width ?? 0}px`); // Width is 0 if no column exists
    }
  }

  _getFirstRowTotalColumnHeaderCell () {
    return (this.#olapPivotTable.isRenderForPrint()
      ? this.#olapPivotTable.querySelector('thead')
      : this.#olapPivotTable.getScrollHeadInner())
      .querySelector('.column-type--PIVOT_ROW_TOTAL');
  }

  /**
   * This function prepares horizontal sorting buttons. Top margin is calculated and added manually
   * because the buttons are decoupled from the table and have absolute positioning. It was implemented
   * like this because of the support for the IE11. (sticky property doesn't work as expected)
   * To find out where to position the buttons, we need to grab the rows in the table header and find out their
   * heights.
   *
   * After that, there are two cases that need to be handled:
   *  1. There is no virtual values dimension.
   *     Columns for buttons will start from the second row, and the first row will be left for
   *     measures.
   *  2. There is virtual values dimension.
   *     Rows are one to one with columnsForButtons and we make sure that if the previous row contained
   *     virtual values dimension we add the height of that row to the top margin as well.
   *
   * @param {(TableColumnDto | undefined)[]} columnsForButtons - Configuration for the buttons that need to be shown.
   * @return {HTMLButtonElement[]} - Returns array of horizontal sorting buttons that can be later rendered in the DOM.
   */
  _createButtons (columnsForButtons) {
    const rowHeights = [ ...this.#olapPivotTable.querySelectorAll('.dt-scroll-body .dt-scroll-head tr') ]
      .map(row => Math.floor(row.getBoundingClientRect().height))
      .filter(height => height > 0); // Ignore table layouting row
    const buttons = [];
    const indexOfVirtualDimension = columnsForButtons.findIndex(column => !column);

    columnsForButtons.forEach((column, index) => {
      // We don't add horizontal sorting buttons for the virtual dimensions row.
      if (index === indexOfVirtualDimension) {
        return;
      }
      let marginTop = 0;
      // If there is no virtual dimension rowHeight for the current column are accessed by rowHeights[index + 1]. (first row is for measures)
      if (indexOfVirtualDimension === -1) {
        // Move the sorting button at the bottom of the row.
        marginTop += rowHeights[index + 1] - HORIZONTAL_SORTING_BUTTONS_HEIGHT;
        // For the first row we add the height of the measures row.
        if (index === 0) {
          marginTop += rowHeights[0];
        }
      } else { // If there is a virtual dimension row, rowHeight for the current column is accessed by rowHeights[index].
        // Move the sorting button at the bottom of the row.
        marginTop += rowHeights[index] - HORIZONTAL_SORTING_BUTTONS_HEIGHT;
        // If the previous column was virtual values dimension add the top margin for that as well.
        if (index > 0 && index - 1 === indexOfVirtualDimension) {
          marginTop += rowHeights[index - 1];
        }
      }

      const order = getSortOrder(this.#olapPivotTable, column);

      const button = this._createSortingFlyoutButton(column, order);
      button.style.marginTop = `${marginTop}px`;

      const verticalColumnIndex = indexOfVirtualDimension === -1 ? index + 1 : index;

      initColumnHeaderContextMenu(
        'horizontal',
        column,
        button,
        () => order,
        newOrder => this._notify(PIVOT_SORT_CHANGE, {
          columnName: column.name,
          columnHierarchyName: column.hierarchyName || column.columnId,
          order: newOrder
        }),
        () => this.#pivotHeaderContextMenuContentProviders.flatMap(p => p(verticalColumnIndex))
      );

      buttons.push(button);
    });

    return buttons;
  }

  _createSortingFlyoutButton (column, order) {
    return createIconButton(
      createHorizontalSortIcon(order),
      column.title,
      {
        variant: 'borderless',
        size: 's'
      });
  }

  /**
   * Returns TableColumnDtos for vertical sort/action buttons.
   * Currently, our information is scattered around in both the displayed pivot columns (individual cells in the
   * multi-row column header) and the configurationColumns of TableConfigurationDto. Our current plan is to get rid
   * of the configurationColumns (at least on workbooks). Because this requires more steps - Including
   * finding a better place to store the origin column information (title, sorting, etc.) - we have to stitch the
   * required information together.
   * @return {TableColumnDto[]}
   */
  _getColumnsForButtons () {
    const columnsForButtonsAsDrawn = this.#getHierarchicalColumnLevelRepresentatives();

    const tableConfiguration = this.#olapPivotTable.tableConfiguration;
    const virtualMeasureDimension = tableConfiguration.pivotConfiguration.virtualValuesDimension;

    // Ensure virtual measure dimension is present
    const columnAttributes = [ ...tableConfiguration.pivotConfiguration.pivotTableConfig.cols ];
    if (!columnAttributes.includes(virtualMeasureDimension)) {
      columnAttributes.unshift(virtualMeasureDimension);
    }

    // Spread to configurationColumns including individual hierarchy levels and undefined for virtual measure dimension
    const configurationColumns = columnAttributes
      .flatMap(columnName =>
        columnName === virtualMeasureDimension
          ? [ undefined ] // Still need an entry for virtual measure column for button positioning and index matching
          : tableConfiguration.configurationColumns
            .filter(configurationColumn => {
              return configurationColumn.name === columnName // Normal column
                || (configurationColumn.hierarchyName === columnName // hierarchy level
                  && this.#isHierarchyLevelPresent(configurationColumn));
            }));

    // Stitch together as new column objects to not corrupt the sources
    return configurationColumns
      .map((configurationColumn, verticalColumnIndex) => {
        if (configurationColumn) { // undefined for virtual measure column
          return Object.assign(
            {},
            configurationColumn,
            {
              subordinatelySorted: columnsForButtonsAsDrawn[verticalColumnIndex].subordinatelySorted
            });
        }
      });
  }

  #isHierarchyLevelPresent (hierarchyLevelConfigurationColumn) {
    return this.#olapPivotTable.tableConfiguration.columnAttributeGroups
      .some(group => group.columnId === hierarchyLevelConfigurationColumn.columnId);
  }

  #getHierarchicalColumnLevelRepresentatives () {
    const tableConfiguration = this.#olapPivotTable.tableConfiguration;
    const firstHierarchicalColumn = tableConfiguration.columns.find(column => column.children?.length);
    const columnsForButtons = [];

    let currentColumn = firstHierarchicalColumn;
    while (currentColumn) {
      columnsForButtons.push(currentColumn);
      currentColumn = currentColumn.children?.[0]; // Take first hierarchical of each vertical level as representative
    }
    return columnsForButtons;
  }

  _notify (eventType, data) {
    this.dispatchEvent(new CustomEvent(eventType, { detail: data, bubbles: true }));
  }

  #createContextMenuContentProvider () {
    return columnIndex => {
      if (columnIndex === 0
        || !this.#olapPivotTable.hasPivotConfiguration()
        || !isRowColumn(this.#olapPivotTable, columnIndex)) {
        return [];
      }

      const columnDto = this.#olapPivotTable.getColumns()[getColumnIndexForBackend(columnIndex)];
      return [
        {
          icon: icon(iconVisibilityOff),
          label: i18n(columnDto.hierarchyName ? 'hideHierarchy' : 'hideColumn'),
          execute: () => {
            const columnClicked = columnDto;
            const elementName = columnClicked.hierarchyName || columnClicked.name;
            const pivotConfiguration = this.#olapPivotTable.pivotConfiguration;
            const pivotTableConfig = {
              cols: pivotConfiguration.pivotTableConfig.cols,
              rows: pivotConfiguration.pivotTableConfig.rows.filter(value => value !== elementName),
              measures: pivotConfiguration.pivotTableConfig.measures
            };
            this._notify(PIVOT_CHANGE, { pivotTableConfig });
          }
        }
      ];
    };
  }

}

customElements.define(COMPONENT_NAME, PivotTableFeatures);

function getSortOrder (resultTable, column) {
  const sortDirections = resultTable.pivotConfiguration.pivotTableConfig.sortDirections;
  const sortDirection = sortDirections[column.name];

  if (sortDirection === undefined) {
    return undefined;
  }

  return sortDirection ? 'asc' : 'desc';
}

export function isValidPivotConfiguration (pivot, pivotConfiguration) {
  return pivot && pivotConfiguration
    && isValidRowsConfig(pivot, pivotConfiguration)
    && isValidColsConfig(pivot, pivotConfiguration)
    && isValidMeasuresConfig(pivot, pivotConfiguration);
}

function isValidRowsConfig (pivot, pivotConfiguration) {
  return isRowsCountMinValid(pivot) && pivot.rows.length <= pivotConfiguration.maxCountDimensionsRows;
}

function isValidColsConfig (pivot, pivotConfiguration) {
  if (!isColsCountMinValid(pivot, pivotConfiguration)) {
    return false;
  }
  const filteredColumnNames = filterVirtualDimension(pivotConfiguration, pivot.cols);
  return filteredColumnNames.length <= pivotConfiguration.maxCountDimensionsColumns;
}

function filterVirtualDimension (pivotConfiguration, columnNames = []) {
  return columnNames.filter(name => name !== pivotConfiguration.virtualValuesDimension);
}

function isValidMeasuresConfig (pivot, pivotConfiguration) {
  return isMeasuresCountMinValid(pivot) && pivot.measures.length <= pivotConfiguration.maxCountMeasures;
}

export function isRowsCountMinValid (pivot) {
  return pivot?.rows?.length > 0;
}

export function isMeasuresCountMinValid (pivot) {
  return pivot?.measures?.length > 0;
}

export function isColsCountMinValid (pivot, pivotConfiguration) {
  return pivot?.cols?.length > 0 && filterVirtualDimension(pivotConfiguration, pivot.cols).length > 0;
}
